import React, { useState } from "react";
import ConnectProBrand from "./ConnectProBrand";
import { DateTime } from "luxon";

const API_URL = process.env.REACT_APP_API_URL;

function formatTime(secondsFloat) {
  if (secondsFloat === null || secondsFloat === undefined) return "--";
  const milliseconds = Math.round(secondsFloat * 1000);
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const ms = milliseconds % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

const rankMedal = (rank) => {
  if (rank === 0) return "🥇";
  if (rank === 1) return "🥈";
  if (rank === 2) return "🥉";
  return rank + 1;
};

const cardStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)",
  padding: "16px 12px",
  marginBottom: "20px",
};

const LeaderboardTable = ({
  title,
  subtitle,
  icon,
  data,
  columns,
  currentPlayer,
  canViewPuzzle,
  canViewGuesses,
  onViewPuzzle,
  puzzleId,
  onNameClick,
  hideHeader = false,
}) => {
  if (data.length === 0) return null;

  return (
    <div style={cardStyle}>
      {!hideHeader && <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "14px", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          {icon === "C" ? <ConnectProBrand size="card" markOnly /> : <span style={{ fontSize: "1.2rem", lineHeight: "1.2" }}>{icon}</span>}
          <div>
            <h2 style={{ margin: "0 0 2px 0", fontSize: "0.95rem", fontWeight: "700", color: "#222" }}>{title}</h2>
            {subtitle && (
              <p style={{ margin: 0, fontSize: "0.75rem", color: "#888", fontWeight: "400" }}>{subtitle}</p>
            )}
          </div>
        </div>
        {canViewPuzzle && puzzleId && (
          <button
            onClick={() => onViewPuzzle(puzzleId)}
            style={{ padding: "6px 12px", fontSize: "0.8rem", fontWeight: "600", color: "#2563eb", backgroundColor: "transparent", border: "1px solid #2563eb", borderRadius: "6px", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            View Puzzle
          </button>
        )}
      </div>}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
        <thead>
          <tr style={{ borderBottom: "1.5px solid #f0f0f0" }}>
            {columns.map((col) => (
              <th key={col.key} style={{ padding: "6px 6px", textAlign: col.align || "left", fontWeight: "600", color: "#999", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em", width: col.width }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((entry, index) => {
            const key = value => value.solved
              ? [true, value.hints_used || 0, value.time_taken]
              : [false, value.hints_used || 0, value.guess_count];
            const firstEqual = data.findIndex(value => JSON.stringify(key(value)) === JSON.stringify(key(entry)));

            const isCurrentPlayer = entry.player_name === currentPlayer;
            const isTop3 = index < 3;
            const hasSubmissions = entry.submissions?.length > 0;
            const canClick = hasSubmissions && onNameClick && canViewGuesses;
            return (
              <tr
                key={index}
                onClick={canClick ? () => onNameClick(entry) : undefined}
                style={{ borderBottom: "1px solid #f8f8f8", backgroundColor: isCurrentPlayer ? "#f0faf0" : "transparent", cursor: canClick ? "pointer" : "default", transition: "background-color 0.15s ease" }}
                onMouseEnter={(e) => { if (canClick) e.currentTarget.style.backgroundColor = isCurrentPlayer ? "#e0f5e0" : "#f5f5f5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isCurrentPlayer ? "#f0faf0" : "transparent"; }}
              >
                <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: isTop3 ? "700" : "500", fontSize: isTop3 ? "1rem" : "0.85rem", color: "#333" }}>
                  {rankMedal(firstEqual)}
                </td>
                <td style={{ padding: "10px 6px", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                  <span style={{ color: isCurrentPlayer ? "#1565C0" : "#333", fontWeight: isCurrentPlayer ? "700" : isTop3 ? "600" : "500" }}>
                    {entry.player_name}
                    {hasSubmissions && <span style={{ marginLeft: "6px", opacity: 0.7 }}>🔍</span>}
                  </span>
                  {isCurrentPlayer && <span style={{ fontSize: "0.65rem", color: "#4CAF50", fontWeight: "700", marginLeft: "4px" }}>you</span>}
                </td>
                {columns.some((c) => c.key === "hints") && (
                  <td style={{ padding: "10px 6px", textAlign: "center", color: "#2196F3", fontWeight: "700", fontSize: "0.9rem", fontVariantNumeric: "tabular-nums", minWidth: "40px", backgroundColor: entry.hints_used > 0 ? "#E3F2FD" : "transparent", borderRadius: "4px" }}>
                    💡 {entry.hints_used}
                  </td>
                )}
                <td style={{ padding: "10px 4px", textAlign: "right", color: isTop3 ? "#111" : "#555", fontWeight: isTop3 ? "600" : "500", fontVariantNumeric: "tabular-nums", fontSize: "0.85rem", minWidth: "48px" }}>
                  {entry.solved ? formatTime(entry.time_taken) : "–"}
                </td>
                <td style={{ padding: "10px 6px", textAlign: "center", color: "#F57C00", fontWeight: "700", fontSize: "0.9rem", fontVariantNumeric: "tabular-nums", minWidth: "40px", backgroundColor: entry.guess_count > 5 ? "#FFF3E0" : "transparent", borderRadius: "4px" }}>
                  🎯 {entry.guess_count}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Leaderboard = ({ leaderboard, selectedDate, hasPlayedToday }) => {
  const currentPlayer = localStorage.getItem("playerName") || "";
  const azToday = DateTime.now().setZone("America/Phoenix").toISODate();
  const isToday = selectedDate === azToday;
  const canViewPuzzle = !isToday || (isToday && hasPlayedToday);

  const [showPuzzleModal, setShowPuzzleModal] = useState(false);
  const [puzzleChain, setPuzzleChain] = useState(null);
  const [loadingPuzzle, setLoadingPuzzle] = useState(false);
  const [showGuessModal, setShowGuessModal] = useState(false);
  const [guessModalData, setGuessModalData] = useState(null);
  const [loadingGuesses, setLoadingGuesses] = useState(false);

  const handleViewPuzzle = async (puzzleId) => {
    setLoadingPuzzle(true);
    try {
      const [puzzleRes, answersRes] = await Promise.all([
        fetch(`${API_URL}/connectpro/puzzle/?puzzle_id=${puzzleId}`),
        fetch(`${API_URL}/connectpro/answers/?puzzle_id=${puzzleId}`),
      ]);
      const puzzleData = await puzzleRes.json();
      const answersData = await answersRes.json();
      setPuzzleChain([puzzleData.start_word, ...(answersData.words || []), puzzleData.end_word]);
      setShowPuzzleModal(true);
    } catch (err) {
      console.error("Error loading puzzle:", err);
    } finally {
      setLoadingPuzzle(false);
    }
  };

  const handleViewGuesses = async (entry) => {
    if (!entry.submissions || entry.submissions.length === 0) return;
    setLoadingGuesses(true);
    try {
      const [puzzleRes, answersRes] = await Promise.all([
        fetch(`${API_URL}/connectpro/puzzle/?puzzle_id=${entry.puzzle_id}`),
        fetch(`${API_URL}/connectpro/answers/?puzzle_id=${entry.puzzle_id}`),
      ]);
      const puzzleData = await puzzleRes.json();
      const answersData = await answersRes.json();
      const chain = [puzzleData.start_word, ...(answersData.words || []), puzzleData.end_word];
      setGuessModalData({ playerName: entry.player_name, solved: entry.solved, chain, submissions: entry.submissions, hints_used: entry.hints_used, guess_count: entry.guess_count, time_taken: entry.time_taken });
      setShowGuessModal(true);
    } catch (err) {
      console.error("Error loading guesses:", err);
    } finally {
      setLoadingGuesses(false);
    }
  };

  // Determine tiers
  const noHintsGroup = leaderboard
    .filter((e) => e.solved && e.hints_used === 0)
    .sort((a, b) => (a.time_taken || Infinity) - (b.time_taken || Infinity));

  const hintsGroup = leaderboard
    .filter((e) => !e.solved || e.hints_used > 0)
    .sort((a, b) => {
      if (a.solved !== b.solved) return b.solved - a.solved;
      if (a.solved && b.solved) {
        if (a.hints_used !== b.hints_used) return a.hints_used - b.hints_used;
        return (a.time_taken || Infinity) - (b.time_taken || Infinity);
      }
      if (a.hints_used !== b.hints_used) return a.hints_used - b.hints_used;
      return a.guess_count - b.guess_count;
    });

  const hasPerfectRuns = noHintsGroup.length > 0;
  const hasHintSolved = hintsGroup.some((e) => e.solved);

  // When no perfect runs, determine the fallback subtitle
  const fallbackSubtitle = hasPerfectRuns
    ? null
    : hasHintSolved
    ? "No perfect runs today — ranked by hints, then time"
    : "No one solved today — ranked by hints used, then guesses";

  const perfectCols = [
    { key: "rank", label: "Rank", align: "center", width: "32px" },
    { key: "name", label: "Name", align: "left" },
    { key: "time", label: "Time", align: "right", width: "65px" },
    { key: "guesses", label: "🎯 Guesses", align: "center", width: "60px" },
  ];

  const hintCols = [
    { key: "rank", label: "Rank", align: "center", width: "32px" },
    { key: "name", label: "Name", align: "left" },
    { key: "hints", label: "💡 Hints", align: "center", width: "60px" },
    { key: "time", label: "Time", align: "right", width: "65px" },
    { key: "guesses", label: "🎯 Guesses", align: "center", width: "60px" },
  ];

  const puzzleId =
    [...noHintsGroup, ...hintsGroup].find((e) => e.puzzle_id)?.puzzle_id || null;

  return (
    <div>
      {/* Daily leaderboard */}
      {!isToday || hasPlayedToday ? (hasPerfectRuns ? (
        <>
          <LeaderboardTable
            title="ConnectPro Leaderboard"
            subtitle="No hints used"
            icon="C"
            data={noHintsGroup}
            columns={perfectCols}
            currentPlayer={currentPlayer}
            canViewPuzzle={canViewPuzzle}
            canViewGuesses={canViewPuzzle}
            onViewPuzzle={handleViewPuzzle}
            puzzleId={puzzleId}
            onNameClick={handleViewGuesses}
            hideHeader
          />
          <LeaderboardTable
            title="Hint-Assisted Runs"
            icon="💡"
            data={hintsGroup}
            columns={hintCols}
            currentPlayer={currentPlayer}
            canViewGuesses={canViewPuzzle}
            canViewPuzzle={canViewPuzzle}
            onViewPuzzle={handleViewPuzzle}
            puzzleId={puzzleId}
            onNameClick={handleViewGuesses}
          />
        </>
      ) : (
        <LeaderboardTable
          title="Leaderboard"
          subtitle={fallbackSubtitle}
          icon="🏆"
          data={hintsGroup}
          columns={hintCols}
          currentPlayer={currentPlayer}
          canViewPuzzle={canViewPuzzle}
          canViewGuesses={canViewPuzzle}
          onViewPuzzle={handleViewPuzzle}
          puzzleId={puzzleId}
          onNameClick={handleViewGuesses}
          hideHeader
        />
      )) : null}

      {/* Puzzle Modal */}
      {showPuzzleModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={() => setShowPuzzleModal(false)}>
          <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", maxWidth: "400px", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: "700" }}>Puzzle Solution</h2>
              <button onClick={() => setShowPuzzleModal(false)} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#999" }}>✕</button>
            </div>
            {loadingPuzzle ? (
              <p style={{ textAlign: "center", color: "#666" }}>Loading...</p>
            ) : puzzleChain ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {puzzleChain.map((word, index) => (
                  <div key={index} style={{ padding: "12px", backgroundColor: "#f0f2f5", borderRadius: "8px", textAlign: "center", fontSize: "1rem", fontWeight: "600", color: "#333", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {word}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: "center", color: "#666" }}>Unable to load puzzle</p>
            )}
            <button onClick={() => setShowPuzzleModal(false)} style={{ width: "100%", marginTop: "16px", padding: "10px", backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: "6px", fontSize: "0.95rem", fontWeight: "600", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* Guess Modal */}
      {showGuessModal && guessModalData && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={() => setShowGuessModal(false)}>
          <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "24px", maxWidth: "360px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700" }}>{guessModalData.playerName}</h2>
              <button onClick={() => setShowGuessModal(false)} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#999" }}>✕</button>
            </div>
            <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
              <p style={{ margin: 0, fontSize: "0.75rem", color: guessModalData.solved ? "#4CAF50" : "#E53935", fontWeight: "600" }}>{guessModalData.solved ? "Solved ✓" : "Did not solve"}</p>
              <p style={{ margin: 0, fontSize: "0.75rem", color: "#666", fontWeight: "600" }}>📝 {guessModalData.guess_count} {guessModalData.guess_count === 1 ? "guess" : "guesses"}</p>
              {guessModalData.hints_used !== undefined && (
                <p style={{ margin: 0, fontSize: "0.75rem", color: guessModalData.hints_used === 0 ? "#4CAF50" : "#FF9800", fontWeight: "600" }}>
                  💡 {guessModalData.hints_used === 0 ? "No hints" : `${guessModalData.hints_used} ${guessModalData.hints_used === 1 ? "hint" : "hints"}`}
                </p>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxHeight: "60vh", overflowY: "auto" }}>
              {guessModalData.submissions.map((submission, subIdx) => {
                const isLastSubmission = subIdx === guessModalData.submissions.length - 1;
                const hintsAtThisPoint = submission.hints_used !== undefined ? submission.hints_used : null;
                const prevHints = subIdx > 0 ? (guessModalData.submissions[subIdx - 1].hints_used || 0) : 0;
                let hintsUsedThisAttempt = 0;
                if (hintsAtThisPoint !== null) {
                  hintsUsedThisAttempt = hintsAtThisPoint - prevHints;
                } else if (isLastSubmission && guessModalData.solved) {
                  hintsUsedThisAttempt = Math.max(0, (guessModalData.hints_used || 0) - prevHints);
                }
                return (
                  <div key={subIdx}>
                    <div style={{ fontSize: "0.7rem", fontWeight: "700", color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        Attempt {subIdx + 1}
                        {isLastSubmission && guessModalData.solved && <span style={{ color: "#4CAF50", marginLeft: "6px" }}>✓ Solved</span>}
                      </div>
                      {hintsUsedThisAttempt > 0 && <span style={{ fontSize: "0.65rem", color: "#FF9800", fontWeight: "600" }}>💡 +{hintsUsedThisAttempt} {hintsUsedThisAttempt === 1 ? "hint" : "hints"}</span>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {guessModalData.chain.map((word, idx) => {
                        const isFixed = idx === 0 || idx === guessModalData.chain.length - 1;
                        if (isFixed) {
                          return <div key={idx} style={{ padding: "8px 14px", backgroundColor: "#f0f2f5", borderRadius: "6px", textAlign: "center", fontSize: "0.85rem", fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: "0.05em" }}>{word}</div>;
                        }
                        const slotIdx = idx - 1;
                        const guess = submission.guesses && submission.guesses[slotIdx];
                        const isCorrect = submission.correct && submission.correct[slotIdx];
                        const isEmpty = !guess || guess.trim() === "";
                        return (
                          <div key={idx} style={{ padding: "8px 14px", backgroundColor: isCorrect ? "#f0faf0" : isEmpty ? "#fafafa" : "#fff8f8", border: `2px solid ${isCorrect ? "#4CAF50" : isEmpty ? "#e0e0e0" : "#ffcdd2"}`, borderRadius: "6px", textAlign: "center", fontSize: "0.85rem", fontWeight: "600", color: isCorrect ? "#2e7d32" : isEmpty ? "#bbb" : "#c62828", textTransform: "uppercase", letterSpacing: "0.05em", fontStyle: isEmpty ? "italic" : "normal" }}>
                            {isEmpty ? "—" : guess}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setShowGuessModal(false)} style={{ width: "100%", marginTop: "16px", padding: "10px", backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: "6px", fontSize: "0.95rem", fontWeight: "600", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
